import { Directive, NgZone, inject, input, output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { SessionService } from 'account-hybrid/core/authentication';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { buildCnameUrl } from 'apps/account/src/account-shared/pipes/cname-url.util';
import { FileSaverService } from 'apps/account/src/account-shared/services/fileSaver.service';
import { LayersApiService } from 'apps/account/src/account-shared/services/layers-api.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { EventBusService } from 'apps/account/src/account-shared/services/event-bus.service';
import dayjs from 'dayjs/esm/index';
import PropertyApi from '../../../../account-v2/src/app/actions/propertyApi';
import { SocialNetwork } from '../../../../account-v2/src/app/api/socialNetwork';
import { ShareType } from '../../../../account-v2/src/app/components/broadcast/shareType';
import { StoryDescriptor } from '../../../../account-v2/src/app/components/broadcast/storyDescriptor';
import { StoriesThunkDispatcherService } from '../story-modal/stories-thunk-dispatcher.service';
import { ShareOAuthService } from './share-oauth.service';

/** Port of the account-v2 `FlowStep` minus the template string (templates live in the host components). */
export class BroadcastFlowStep {
    constructor(public name: string, public isActive: boolean = false) {}
}

/**
 * v1 `LeadShareImageActions` (flows/shareImageActions.ts) — the only reachable
 * IShareImageActions (the v1 `setShareType` instantiate hard-codes it; the
 * Invitation variant was commented out). PhotoContestApiService's
 * `downloadShareImage` resolves `{ content, fileName }` exactly like the v1
 * `PhotoContest.downloadShareImage` (the inc 2b-1 verbatim port).
 */
export class LeadShareImageActions {
    constructor(
        private readonly photoContest: PhotoContestApiService,
        private readonly fileSaver: FileSaverService,
        private readonly spacesService: SpacesService,
    ) {}

    createImageTemplate(_model: any): never {
        throw new Error('Method not implemented.');
    }

    createImage(model: any): Promise<any> {
        return this.photoContest.createCurationShareImage(this.spacesService.getPropertyUuid()!, model);
    }

    downloadImage(imageUrl: any): Promise<void> {
        return this.photoContest.downloadShareImage(this.spacesService.getPropertyUuid()!, imageUrl).then((data: any) => {
            this.fileSaver.saveBlob(data.content, data.fileName);
        });
    }
}

/**
 * WS5 #10 Stage A (BroadcastModal port) — native port of the account-v2
 * `BroadcastStoryFlowController` (flows/controllers/broadcastStoryFlowController.ts),
 * the shared base of the three broadcast flows. The three host components extend
 * this; their templates are the ports of the three ComponentFlowBuilder template
 * strings (the `ft-flow`/`ft-step`/`ng-if="$ftStep.step.isActive"` machinery
 * collapses to per-step `*ngIf="getStep('X').isActive"` divs).
 *
 * Dependency mapping (v1 → native): PhotoContest→PhotoContestApiService,
 * Layers→LayersApiService, parse→ParseService, $q→Promise,
 * $filter('ftAzureStoredPhoto')→buildAzureStoredPhotoUrl,
 * $filter('cnameUrl')→buildCnameUrl, $filter('translate')→TranslateService.instant,
 * $filter('daysUntilDate')→the same date arithmetic inline (dayjs),
 * shareOAuth→ShareOAuthService (ported), fileSaver→FileSaverService,
 * $ngRedux→StoriesThunkDispatcherService (the recommendation thunks destructure
 * only `{ $http, environment }` — both native),
 * $rootScope.$broadcast('[StoryModal].RecommendationComplete')→EventBusService
 * (both sides are native now — the last cross-boundary holdout moves to the bus),
 * $rootScope.$apply→NgZone.run (the OAuth popup callback arrives out-of-zone).
 *
 * PRESERVE-BUGS kept verbatim: `downloadShareImage` passes the raw
 * `this.$rootScope.$broadcast(...)` RESULT to `.then` in v1 (line 130: called
 * immediately, not in a callback) — replicated by emitting BEFORE the then-chain
 * resolves, i.e. emit immediately after dispatch like v1 did.
 */
@Directive()
export abstract class BroadcastFlowBase {
    static readonly STEP_NAME_CHOOSE_GOAL = 'ChooseGoal';
    static readonly STEP_NAME_STORY_IMAGE_SETUP = 'StoryShareImageSetup';
    static readonly STEP_NAME_PHOTOCONTEST_INVITATION_IMAGE_SETUP = 'PhotocontestInvitationShareImageSetup';
    static readonly STEP_NAME_CHOOSE_NETWORK = 'ChooseNetwork';
    static readonly STEP_NAME_SHARE_IMAGE_READY = 'ShareImageReady';
    static readonly STEP_NAME_CREATE_IMAGE_PROGRESS = 'CreateImageProgress';
    static readonly STEP_NAME_CONCLUDE_RECOMMENDATION = 'ConcludeRecommendation';

    static readonly RecommendationTypeToShareTypeMap: Record<number, ShareType> = {
        1: ShareType.Story,
        2: ShareType.PhotoContestInvitation,
        5: ShareType.PhotoContestInvitation,
    };

    readonly propertyUuid = input.required<string>();
    readonly storyUuid = input.required<string>();
    /** The launcher destroys the mounted host when this fires (v1 `renderer.remove()`). */
    readonly closed = output<void>();

    protected readonly photoContest = inject(PhotoContestApiService);
    protected readonly layersApi = inject(LayersApiService);
    protected readonly parse = inject(ParseService);
    protected readonly spacesService = inject(SpacesService);
    protected readonly sessionService = inject(SessionService);
    protected readonly fileSaver = inject(FileSaverService);
    protected readonly dispatcher = inject(StoriesThunkDispatcherService);
    protected readonly eventBus = inject(EventBusService);
    protected readonly translateService = inject(TranslateService);
    protected readonly zone = inject(NgZone);
    protected readonly environment = inject(ENVIRONMENT_TOKEN) as Environment;
    protected readonly shareOAuth = inject(ShareOAuthService);

    recommendation: any = null;

    fullContext: string | undefined;
    property: any;
    story: any;
    isQuoteOnly = false;

    shareType: ShareType | undefined;
    imageSetupOptions: any;

    contests: any;
    contest: any;

    shareUrl: any;

    network: any;
    inviteToContest: any;
    shareImageUrl: any;
    storySharedTo: any;

    storyDescriptor: StoryDescriptor | undefined;
    shareImageActions: LeadShareImageActions | undefined;
    shareImageTemplateData: any;

    isInitialized = false;

    steps: BroadcastFlowStep[] = [];

    /** v1 `$filter('ftAzureStoredPhoto')`. */
    protected ftAzureStoredPhoto = (photo: any, size?: string): string =>
        buildAzureStoredPhotoUrl(photo, size, this.environment.AzureCdnBaseUrl) as string;

    /** v1 `$filter('daysUntilDate')` (story-modal.pipes port of the same v1 filter). */
    protected daysUntilDate = (end: any, start?: any): number => {
        const startDate = start ? dayjs(start) : dayjs();
        return dayjs(end).diff(startDate.subtract(1, 'days'), 'days');
    };

    protected translate(key: string): string {
        return this.translateService.instant(key);
    }

    close(): void {
        // v1: ftModal.destroy(<id>) + renderer.remove() — natively the launcher
        // destroys the body-mounted host component (DOM + registry die together).
        this.closed.emit();
    }

    setShareType(type: ShareType): void {
        this.shareType = type;
        this.shareImageActions = new LeadShareImageActions(this.photoContest, this.fileSaver, this.spacesService);
    }

    createShareImageTemplate(): void {
        this.shareImageActions!.createImageTemplate({
            SocialApplication: 1,
            ContestUUID: this.story.guest.poststay.photo.photoContest.uuid,
            LanguageCode: 'en',
        });
    }

    createShareImage(model: any): any {
        if (this.shareType == ShareType.Story || this.shareType == ShareType.Website) {
            return this.photoContest
                .createCurationShareImage(this.spacesService.getPropertyUuid()!, model)
                .then((shareImageUrl: any) => (this.shareImageUrl = shareImageUrl));
        } else if (this.shareType == ShareType.PhotoContestInvitation) {
            this.photoContest
                .createInvitationShareImageFromTemplate(this.spacesService.getPropertyUuid()!, model)
                .then((shareImageUrl: any) => (this.shareImageUrl = shareImageUrl));
        }
    }

    openOAuth(message: any): void {
        if (this.shareType == ShareType.Story || this.shareType == ShareType.Website) {
            this.doCurationShare(message);
        } else {
            this.doContestInvitationShare(message);
        }
    }

    downloadShareImage(): void {
        this.shareImageActions!.downloadImage(this.shareImageUrl).then(() => {
            this.zone.run(() => {
                const network = this.story.socialNetworksSharing.find((n: any) => n.socialNetworkType == this.network.type);
                const networkType = this.parse.int(network.socialNetworkType);
                if (networkType == SocialNetwork.Instagram) {
                    if (!this.storySharedTo.includes(networkType)) {
                        this.storySharedTo.push(networkType);
                    }
                    if (!!this.recommendation) {
                        // v1 quirk kept: the broadcast fired IMMEDIATELY (passed as the
                        // already-invoked `.then(this.$rootScope.$broadcast(...))` argument),
                        // not after closeRecommendation resolved.
                        this.dispatcher.dispatch(
                            PropertyApi.closeRecommendation(this.property.id, this.recommendation.recommendation_uuid, true),
                        );
                        this.eventBus.emit('[StoryModal].RecommendationComplete');
                    }
                    this.toggleStep(BroadcastFlowBase.STEP_NAME_SHARE_IMAGE_READY, false);
                    this.toggleStep(BroadcastFlowBase.STEP_NAME_CHOOSE_NETWORK, true);
                }
            });
        });
    }

    chooseDifferentChannel(): void {
        this.toggleStep(BroadcastFlowBase.STEP_NAME_SHARE_IMAGE_READY, false);
        this.toggleStep(BroadcastFlowBase.STEP_NAME_CHOOSE_NETWORK, true);
    }

    createImageSetupOptionsFromStory(story: any): any {
        const storyDescriptor = new StoryDescriptor(this.parse, story);
        return {
            isShowGuestPhoto: !!story.guest.poststay.photo,
            isShowProfilePic: !!story.guest.profileImageUrl,
            isShowGuestName: story.guest.first && story.guest.first.length > 0,
            isShowGuestQuote:
                !storyDescriptor.isHotelRatedNegativeQuote() &&
                !storyDescriptor.isHotelRatedNeutralQuote() &&
                storyDescriptor.hasPositiveQuote() &&
                !storyDescriptor.isCaptionBetterThanQuote(),
            isShowPhotoCaption:
                !storyDescriptor.isHotelRatedNegativeCaption() &&
                !storyDescriptor.isHotelRatedNeutralCaption() &&
                storyDescriptor.hasCaption() &&
                storyDescriptor.isCaptionBetterThanQuote(),
            isLandscape: storyDescriptor.isLandscape(),
            isRtl: storyDescriptor.isRtl(),
        };
    }

    getShareImageModel(): any {
        if (this.shareType == ShareType.Story || this.shareType == ShareType.Website || this.shareType == ShareType.PhotoContestInvitation) {
            return {
                GuestUUID: this.story.guest.uuid,
                SocialApplication: this.network.type,
                PhotoUUID: this.imageSetupOptions.isShowGuestPhoto ? this.story.guest.poststay.photo.uuid : null,
                ...this.imageSetupOptions,
                SelectedPhotoUrl: this.imageSetupOptions.isShowGuestPhoto
                    ? this.ftAzureStoredPhoto(this.story.guest.poststay.photo)
                    : this.property.BackgroundImageUrl,
            };
        }
    }

    getShareUrl(): any {
        switch (this.shareType) {
            case ShareType.PhotoContestInvitation: {
                return buildCnameUrl(
                    this.contest.subdomains,
                    3,
                    this.story.propertySlug,
                    { contest: this.inviteToContest.slug, source: this.network.title, lang: 'en' },
                    (this.environment as any).FliptoHubDomain,
                );
            }
            case ShareType.Story: {
                return `${buildCnameUrl(this.contest.subdomains, 1, this.story.propertySlug, undefined, (this.environment as any).FliptoHubDomain)}/gallery/${this.story.guest.poststay.photo.uuid}`;
            }
            case ShareType.Website: {
                return this.property.propertyUrl;
            }
            default: {
                throw 'Share type not supported';
            }
        }
    }

    openShareModal(network: any): void {
        this.network = network;
        this.shareUrl = this.getShareUrl();

        this.toggleStep(BroadcastFlowBase.STEP_NAME_CHOOSE_NETWORK, false);
        this.toggleStep(BroadcastFlowBase.STEP_NAME_CREATE_IMAGE_PROGRESS, true);

        this.shareImageActions!.createImage(this.getShareImageModel()).then((shareImageUrl: any) => {
            this.zone.run(() => {
                this.shareImageUrl = shareImageUrl;
                this.toggleStep(BroadcastFlowBase.STEP_NAME_CREATE_IMAGE_PROGRESS, false);
                this.toggleStep(BroadcastFlowBase.STEP_NAME_SHARE_IMAGE_READY, true);
            });
        });
    }

    doCurationShare(message: any): void {
        const model: any = {
            companyCode: this.story.companyCode,
            languageCode: 'en',
            socialApp: this.network.type,
            shareMessage: message,
            photoUUID: !!this.story.guest.poststay.photo ? this.story.guest.poststay.photo.uuid : null,
            shareImageUrl: this.shareImageUrl,
            callback: 'fliptoCurationShareComplete',
        };

        if (this.shareType == ShareType.Website) {
            model['websiteShareUrl'] = this.property.propertyUrl;
        }

        this.shareOAuth.log('curation-share', { action: 'open', state: model });
        this.shareOAuth.tryOpenWindow(this.network.type);
        this.shareOAuth.trySetLocation(this.network.type, model);

        (window as any)['fliptoCurationShareComplete'] = (state: any) => {
            this.shareOAuth.log('curation-share', { action: 'complete', state });
            this.zone.run(() => {
                if (state == 0 || state == false || typeof state === 'string') {
                    alert(this.translate('flipto.account.CurationShareError'));
                } else if (state == 1 || state == true) {
                    const network = this.story.socialNetworksSharing.find((n: any) => n.socialNetworkType == this.network.type);
                    const networkType = this.parse.int(network.socialNetworkType);
                    if (!this.storySharedTo.includes(networkType)) {
                        this.storySharedTo.push(networkType);
                    }
                    if (!!this.recommendation) {
                        this.dispatcher
                            .dispatch(PropertyApi.closeRecommendation(this.property.id, this.recommendation.recommendation_uuid, true))
                            .then(() => this.eventBus.emit('[StoryModal].RecommendationComplete'));
                    }
                }
                this.shareOAuth.close();
                this.toggleStep(BroadcastFlowBase.STEP_NAME_SHARE_IMAGE_READY, false);
                this.toggleStep(BroadcastFlowBase.STEP_NAME_CHOOSE_NETWORK, true);
            });
        };
    }

    doContestInvitationShare(message: any): void {
        this.shareOAuth.tryOpenWindow(this.network.type);
        const model = {
            contestUUID: this.inviteToContest.id,
            companyCode: this.story.companyCode,
            languageCode: 'en',
            socialApp: this.network.type,
            shareMessage: message,
            shareImageUrl: this.shareImageUrl,
            invitationUrl: this.shareUrl,
            callback: 'fliptoInvitationShareComplete',
        };

        this.shareOAuth.log('invitation-share', { action: 'open', state: model });
        this.shareOAuth.trySetLocation(this.network.type, model);

        (window as any)['fliptoInvitationShareComplete'] = (state: any) => {
            this.shareOAuth.log('invitation-share', { action: 'complete', state });
            this.zone.run(() => {
                if (state == 0 || state == false) {
                    window.alert(this.translate('flipto.account.CurationShareError'));
                } else if (state == 1 || state == true) {
                    const network = this.story.socialNetworksSharing.find((n: any) => n.socialNetworkType == this.network.type);
                    const networkType = this.parse.int(network.socialNetworkType);
                    if (!this.storySharedTo.includes(networkType)) {
                        this.storySharedTo.push(networkType);
                    }
                    if (!!this.recommendation) {
                        this.dispatcher
                            .dispatch(PropertyApi.closeRecommendation(this.property.id, this.recommendation.recommendation_uuid, true))
                            .then(() => this.eventBus.emit('[StoryModal].RecommendationComplete'));
                    }
                    window.alert(this.translate('flipto.account.ShareSuccess'));
                }
                this.shareOAuth.close();
                this.toggleStep(BroadcastFlowBase.STEP_NAME_SHARE_IMAGE_READY, false);
                this.toggleStep(BroadcastFlowBase.STEP_NAME_CHOOSE_NETWORK, true);
            });
        };
    }

    reprocessImage(): void {
        this.toggleStep(BroadcastFlowBase.STEP_NAME_SHARE_IMAGE_READY, false);
        this.toggleStep(BroadcastFlowBase.STEP_NAME_CREATE_IMAGE_PROGRESS, true);

        this.shareImageActions!.createImage(this.getShareImageModel()).then((shareImageUrl: any) => {
            this.zone.run(() => {
                this.shareImageUrl = shareImageUrl;
                this.toggleStep(BroadcastFlowBase.STEP_NAME_CREATE_IMAGE_PROGRESS, false);
                this.toggleStep(BroadcastFlowBase.STEP_NAME_SHARE_IMAGE_READY, true);
            });
        });
    }

    getActiveStep(): BroadcastFlowStep | undefined {
        return this.steps.find((s) => s.isActive);
    }

    getStep(name: string): BroadcastFlowStep {
        return this.steps.find((s) => s.name === name)!;
    }

    /** Template-safe step gate (tolerates the pre-ngOnInit empty steps array). */
    stepActive(name: string): boolean {
        return !!this.steps.find((s) => s.name === name)?.isActive;
    }

    toggleStep(name: string, isActive: boolean): void {
        this.getStep(name).isActive = isActive;
    }

    moveTo(nextStep: string): void {
        this.getActiveStep()!.isActive = false;
        this.getStep(nextStep).isActive = true;
    }
}
